import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, ArrowRight, ArrowLeft, CheckCircle, Lightbulb, Target, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TooltipStep {
  id: number | string;
  target: string; // CSS selector for the target element
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'auto';
  action?: 'click' | 'hover' | 'focus' | 'none';
  category: 'navigation' | 'features' | 'workflow' | 'tips';
  role?: string[]; // Show only for specific roles
  page?: string; // Show only on specific pages
  prerequisites?: string[]; // Other tooltip IDs that should be completed first
}

export interface OnboardingTour {
  id: number | string;
  name: string;
  description: string;
  category: 'getting-started' | 'advanced-features' | 'workflows' | 'role-specific';
  estimatedTime: number; // in minutes
  steps: TooltipStep[];
  targetRoles?: string[];
  autoStart?: boolean;
}

interface TooltipOnboardingProps {
  children: React.ReactNode;
  isVisible: boolean;
  currentStep: TooltipStep | null;
  currentTour: OnboardingTour | null;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onComplete: () => void;
  onDismiss: () => void;
}

interface TooltipPopoverProps {
  step: TooltipStep;
  stepIndex: number;
  totalSteps: number;
  tour: OnboardingTour;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onComplete: () => void;
  onDismiss: () => void;
  position: { top: number; left: number; width: number; height: number };
}

function TooltipPopover({
  step,
  stepIndex,
  totalSteps,
  tour,
  onNext,
  onPrevious,
  onSkip,
  onComplete,
  onDismiss,
  position
}: TooltipPopoverProps) {
  const isLastStep = stepIndex === totalSteps - 1;
  const isFirstStep = stepIndex === 0;

  // Calculate tooltip position  
  const calculateTooltipPosition = () => {
    const isMobile = window.innerWidth < 768;
    const tooltipWidth = isMobile ? Math.min(window.innerWidth - 32, 280) : 320;
    const tooltipHeight = isMobile ? 180 : 200;
    const margin = isMobile ? 8 : 16;
    const noOverlapMargin = isMobile ? 20 : 40; // Extra space to avoid blocking target element

    let tooltipX = position.left;
    let tooltipY = position.top;

    // Adjust position based on step position preference
    switch (step.position) {
      case 'top':
        tooltipY = position.top - tooltipHeight - margin - noOverlapMargin;
        tooltipX = position.left + (position.width / 2) - (tooltipWidth / 2);
        break;
      case 'bottom':
        tooltipY = position.top + position.height + margin + noOverlapMargin;
        tooltipX = position.left + (position.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        tooltipX = position.left - tooltipWidth - margin - noOverlapMargin;
        tooltipY = position.top + (position.height / 2) - (tooltipHeight / 2);
        break;
      case 'right':
        tooltipX = position.left + position.width + margin + noOverlapMargin;
        tooltipY = position.top + (position.height / 2) - (tooltipHeight / 2);
        break;
      case 'auto':
      default:
        // Auto-position based on viewport space, prioritizing positions that don't overlap
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        // Calculate distances to edges to find best non-overlapping position
        const spaceTop = position.top;
        const spaceBottom = viewportHeight - (position.top + position.height);
        const spaceLeft = position.left;
        const spaceRight = viewportWidth - (position.left + position.width);

        // On mobile, prefer bottom positioning to avoid blocking elements
        if (isMobile) {
          if (spaceBottom > tooltipHeight + margin + noOverlapMargin) {
            // Bottom position for mobile
            tooltipY = position.top + position.height + margin + noOverlapMargin;
            tooltipX = margin; // Always start from left margin on mobile
          } else if (spaceTop > tooltipHeight + margin + noOverlapMargin) {
            // Top position for mobile
            tooltipY = position.top - tooltipHeight - margin - noOverlapMargin;
            tooltipX = margin; // Always start from left margin on mobile
          } else {
            // For mobile, place tooltip at bottom of screen with safe area
            tooltipY = Math.max(20, viewportHeight - tooltipHeight - 40);
            tooltipX = margin;
          }
        } else {
          // Desktop positioning logic
          // Prioritize top position for large elements, then find best fit
          if (spaceTop > tooltipHeight + margin + noOverlapMargin && position.height > 300) {
            // Top position for large elements
            tooltipY = position.top - tooltipHeight - margin - noOverlapMargin;
            tooltipX = position.left + (position.width / 2) - (tooltipWidth / 2);
          } else if (spaceBottom > tooltipHeight + margin + noOverlapMargin && spaceBottom >= Math.max(spaceTop, spaceLeft, spaceRight)) {
            // Bottom position
            tooltipY = position.top + position.height + margin + noOverlapMargin;
            tooltipX = position.left + (position.width / 2) - (tooltipWidth / 2);
          } else if (spaceTop > tooltipHeight + margin + noOverlapMargin && spaceTop >= Math.max(spaceLeft, spaceRight)) {
            // Top position
            tooltipY = position.top - tooltipHeight - margin - noOverlapMargin;
            tooltipX = position.left + (position.width / 2) - (tooltipWidth / 2);
          } else if (spaceRight > tooltipWidth + margin + noOverlapMargin && spaceRight >= spaceLeft) {
            // Right position
            tooltipX = position.left + position.width + margin + noOverlapMargin;
            tooltipY = position.top + (position.height / 2) - (tooltipHeight / 2);
          } else {
            // Left position (fallback)
            tooltipX = position.left - tooltipWidth - margin - noOverlapMargin;
            tooltipY = position.top + (position.height / 2) - (tooltipHeight / 2);
          }
        }
        break;
    }

    // Ensure tooltip stays within viewport with extra margin for buttons
    if (isMobile) {
      // For mobile, ensure tooltip is always visible and not cut off
      tooltipX = Math.max(margin, Math.min(tooltipX, window.innerWidth - tooltipWidth - margin));
      tooltipY = Math.max(20, Math.min(tooltipY, window.innerHeight - tooltipHeight - 20));
    } else {
      tooltipX = Math.max(margin, Math.min(tooltipX, window.innerWidth - tooltipWidth - margin));
      tooltipY = Math.max(margin, Math.min(tooltipY, window.innerHeight - tooltipHeight - 80)); // Extra margin for buttons
    }

    return { x: tooltipX, y: tooltipY, width: tooltipWidth, height: tooltipHeight, isMobile };
  };

  const tooltipData = calculateTooltipPosition();
  const tooltipPos = { x: tooltipData.x, y: tooltipData.y };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'navigation':
        return <Target className="h-4 w-4" />;
      case 'features':
        return <Lightbulb className="h-4 w-4" />;
      case 'workflow':
        return <ArrowRight className="h-4 w-4" />;
      case 'tips':
        return <HelpCircle className="h-4 w-4" />;
      default:
        return <Lightbulb className="h-4 w-4" />;
    }
  };

  // Mobile-specific rendering
  if (tooltipData.isMobile) {
    return (
      <>
        {/* Simple dark overlay for mobile */}
        <div 
          className="fixed inset-0 bg-black/60" 
          style={{ zIndex: 99998 }}
        />

        {/* Target highlight for mobile */}
        <div
          className="fixed border-4 border-blue-500 bg-blue-500/20 rounded-lg animate-pulse pointer-events-none"
          style={{
            top: position.top - 8,
            left: position.left - 8,
            width: position.width + 16,
            height: position.height + 16,
            zIndex: 99999,
            animationDuration: '2s',
            boxShadow: '0 0 20px rgba(59, 130, 246, 0.5)'
          }}
        />

        {/* Additional inner highlight for better precision */}
        <div
          className="fixed border-2 border-blue-400 rounded-lg pointer-events-none"
          style={{
            top: position.top - 2,
            left: position.left - 2,
            width: position.width + 4,
            height: position.height + 4,
            zIndex: 99999,
            backgroundColor: 'rgba(59, 130, 246, 0.1)'
          }}
        />

        {/* Mobile Tooltip - Bottom positioned */}
        <div
          className="fixed bottom-4 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border pointer-events-auto"
          style={{ zIndex: 999999 }}
        >
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <div className={cn(
                  "p-1.5 rounded-md",
                  step.category === 'navigation' && "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400",
                  step.category === 'features' && "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400",
                  step.category === 'workflow' && "bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400",
                  step.category === 'tips' && "bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400"
                )}>
                  {getCategoryIcon(step.category)}
                </div>
                <div>
                  <h3 className="text-sm font-medium text-foreground">{step.title}</h3>
                  <p className="text-xs text-muted-foreground">
                    Step {stepIndex + 1} of {totalSteps} • {tour.name}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDismiss}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              {step.content}
            </p>

            {/* Progress bar */}
            <div className="mb-4">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>Progress</span>
                <span>{Math.round(((stepIndex + 1) / totalSteps) * 100)}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
                />
              </div>
            </div>

            {/* Mobile buttons */}
            <div className="flex flex-col space-y-2">
              <Button
                onClick={isLastStep ? onComplete : onNext}
                className="w-full h-12 text-base"
              >
                {isLastStep ? (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Complete Tour
                  </>
                ) : (
                  <>
                    Next Step
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
              <div className="flex space-x-2">
                {!isFirstStep && (
                  <Button
                    variant="outline"
                    onClick={onPrevious}
                    className="flex-1 h-10"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                )}
                <Button
                  variant="ghost"
                  onClick={onSkip}
                  className="flex-1 h-10 text-muted-foreground"
                >
                  Skip Tour
                </Button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Desktop rendering
  return (
    <>
      {/* Backdrop overlay with rectangular cutout */}
      <div className="fixed inset-0 z-[9998]">
        {/* Top */}
        <div 
          className="absolute bg-black/60 transition-all duration-200"
          style={{
            top: 0,
            left: 0,
            right: 0,
            height: Math.max(0, position.top - 8)
          }}
        />
        {/* Bottom */}
        <div 
          className="absolute bg-black/60 transition-all duration-200"
          style={{
            top: position.top + position.height + 8,
            left: 0,
            right: 0,
            bottom: 0
          }}
        />
        {/* Left */}
        <div 
          className="absolute bg-black/60 transition-all duration-200"
          style={{
            top: position.top - 8,
            left: 0,
            width: Math.max(0, position.left - 8),
            height: position.height + 16
          }}
        />
        {/* Right */}
        <div 
          className="absolute bg-black/60 transition-all duration-200"
          style={{
            top: position.top - 8,
            left: position.left + position.width + 8,
            right: 0,
            height: position.height + 16
          }}
        />
      </div>

      {/* Target highlight - rectangular border */}
      <div
        className="fixed border-2 border-primary bg-primary/10 rounded-lg z-[9999] animate-pulse pointer-events-none transition-all duration-200"
        style={{
          top: position.top - 4,
          left: position.left - 4,
          width: position.width + 8,
          height: position.height + 8,
          animationDuration: '2s'
        }}
      />

      {/* Desktop Tooltip */}
      <Card
        className="fixed w-80 shadow-xl animate-in fade-in-0 zoom-in-95 duration-200 pointer-events-auto bg-background border"
        style={{
          top: tooltipPos.y,
          left: tooltipPos.x,
          zIndex: 999999,
        }}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={cn(
                "p-1.5 rounded-md",
                step.category === 'navigation' && "bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400",
                step.category === 'features' && "bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-400",
                step.category === 'workflow' && "bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-400",
                step.category === 'tips' && "bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-400"
              )}>
                {getCategoryIcon(step.category)}
              </div>
              <div>
                <CardTitle className="text-sm font-medium">{step.title}</CardTitle>
                <CardDescription className="text-xs">
                  Step {stepIndex + 1} of {totalSteps} • {tour.name}
                </CardDescription>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onDismiss}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {step.content}
          </p>

          {/* Progress bar */}
          <div className="mb-4">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Progress</span>
              <span>{Math.round(((stepIndex + 1) / totalSteps) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
              <div
                className="bg-primary h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          {/* Desktop buttons */}
          <div className="flex items-center justify-between">
            <div className="flex space-x-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onPrevious}
                  className="h-8"
                >
                  <ArrowLeft className="h-3 w-3 mr-1" />
                  Back
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onSkip}
                className="h-8 text-gray-500"
              >
                Skip Tour
              </Button>
            </div>

            <Button
              size="sm"
              onClick={isLastStep ? onComplete : onNext}
              className="h-8"
            >
              {isLastStep ? (
                <>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Complete
                </>
              ) : (
                <>
                  Next
                  <ArrowRight className="h-3 w-3 ml-1" />
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

export function TooltipOnboarding({
  children,
  isVisible,
  currentStep,
  currentTour,
  stepIndex,
  totalSteps,
  onNext,
  onPrevious,
  onSkip,
  onComplete,
  onDismiss
}: TooltipOnboardingProps) {
  const [targetPosition, setTargetPosition] = useState<{ top: number; left: number; width: number; height: number } | null>(null);

  useEffect(() => {
    if (isVisible && currentStep) {
    // Add CSS class to force sidebar visibility during onboarding
    document.body.classList.add('onboarding-active');

    // Lock scrolling and interaction during onboarding but allow zoom
    document.body.style.overflow = 'hidden';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';
    document.body.style.touchAction = 'pinch-zoom';

    // Add specific class when targeting sidebar-related elements
    const isSidebarTarget = currentStep.target.includes('sidebar') || currentStep.target.includes('organization-display');
    if (isSidebarTarget) {
      document.body.classList.add('targeting-sidebar');
    } else {
      document.body.classList.remove('targeting-sidebar');
    }

    let retryCount = 0;
    const maxRetries = 50; // Try for 5 seconds

    const updateTargetPosition = () => {
      const targetElement = document.querySelector(currentStep.target);

      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();

        // For specific elements, adjust positioning for better mobile highlighting
        let adjustedRect = rect;

        // Special handling for notifications - target the actual button inside the wrapper
        if (currentStep.target.includes('notifications')) {
          // Find the button inside the wrapper div
          const notificationButton = targetElement.querySelector('button');
          if (notificationButton) {
            adjustedRect = notificationButton.getBoundingClientRect();
          } else {
            adjustedRect = rect;
          }
        }

        // Special handling for create-assessment - target the button element directly
        if (currentStep.target.includes('create-assessment')) {
          // The target element should be the button itself
          if (targetElement.tagName === 'BUTTON') {
            // Use the button rect directly
            adjustedRect = rect;
          } else {
            // Fallback: look for button inside if it's a wrapper
            const button = targetElement.querySelector('button');
            if (button) {
              adjustedRect = button.getBoundingClientRect();
            } else {
              adjustedRect = rect;
            }
          }
        }

        // Use fixed positioning that responds to viewport changes
        const position = {
          top: adjustedRect.top,
          left: adjustedRect.left,
          width: adjustedRect.width,
          height: adjustedRect.height,
        };
        setTargetPosition(position);

        // Auto-scroll only when element is significantly out of view and only for specific targets
        const shouldAutoScroll = currentStep.target.includes('create-assessment');

        if (shouldAutoScroll) {
          const viewportHeight = window.innerHeight;
          const elementBottom = rect.bottom;
          const elementTop = rect.top;

          // Check if element is out of view (need more lenient check for create-assessment)
          const isOutOfView = elementBottom > viewportHeight || elementTop < 0;

          if (isOutOfView) {
            // Temporarily enable scrolling for the scroll action
            document.body.style.overflow = 'auto';

            // Scroll to bring element into view
            setTimeout(() => {
              targetElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center',
                inline: 'nearest'
              });

              // Re-lock scrolling after scroll completes and update position
              setTimeout(() => {
                document.body.style.overflow = 'hidden';
                updateTargetPosition(); // Refresh position after scroll
              }, 1000);
            }, 300);
          }
        }
      } else {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(updateTargetPosition, 200);
        }
      }
    };

    updateTargetPosition();

    // Update position on resize and zoom changes
    const handleResize = () => {
      setTimeout(updateTargetPosition, 100); // Small delay for zoom events
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(updateTargetPosition, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Listen for zoom events (approximated through scroll events when locked)
    let zoomTimeout: NodeJS.Timeout;
    const handleZoom = () => {
      clearTimeout(zoomTimeout);
      zoomTimeout = setTimeout(updateTargetPosition, 150);
    };

    window.addEventListener('wheel', handleZoom, { passive: true });
    window.addEventListener('touchend', handleZoom, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('wheel', handleZoom);
      window.removeEventListener('touchend', handleZoom);
      clearTimeout(zoomTimeout);

      // Cleanup styles when component unmounts
      document.body.classList.remove('onboarding-active', 'targeting-sidebar');
      document.body.style.overflow = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.body.style.touchAction = '';
    };
  } else {
      setTargetPosition(null);
      // Remove onboarding CSS when not active
      document.body.classList.remove('onboarding-active', 'targeting-sidebar');
      document.body.style.overflow = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
      document.body.style.touchAction = '';
  }
  }, [isVisible, currentStep]);

  if (!isVisible || !currentStep || !currentTour || !targetPosition) {
    return <>{children}</>;
  }

  return (
    <>
      {children}
      <TooltipPopover
        step={currentStep}
        stepIndex={stepIndex}
        totalSteps={totalSteps}
        tour={currentTour}
        onNext={onNext}
        onPrevious={onPrevious}
        onSkip={onSkip}
        onComplete={onComplete}
        onDismiss={onDismiss}
        position={targetPosition}
      />
    </>
  );
}